import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import {
  BillingInterval,
  BillingSummary,
  EndpointUsage,
  PriceTier,
  createBillingCheckoutSession,
  createBillingPortalSession,
  getBillingSummary,
  getEndpointUsage,
  graduatedBreakdown,
  graduatedTotal,
  startTrial,
} from '../services/api'
import { useAuthStore } from '../store/authStore'
import { resetPackagingEntitlementCache } from '../hooks/usePackagingEntitlement'
import { errorText } from '../utils/errorText'
import { localDate, utcCalendarDate } from '../utils/date'
import { DEFAULT_TRIAL_DAYS } from '../utils/subscriptionBanner'

/** Endpoint usage past this fraction of the licensed count is called out. */
const USAGE_WARN_AT = 0.9

/** Matches the backend ceiling; the top pricing band itself is unbounded. */
const MAX_ENDPOINTS = 999999

/** Fallback only — the real figure comes from the summary. */
const DEFAULT_FREE_ENDPOINT_LIMIT = 150

const formatMoney = (minorUnits: number | null, currency: string | null): string => {
  if (minorUnits === null || !currency) return '—'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100)
}

/**
 * An exact amount in minor units — a band's rate or a band's subtotal. Up to
 * three decimals because the Scale band is $0.225 — rounding it to cents would
 * print a rate nobody is charged.
 */
const formatRate = (unitAmount: string | number, currency: string | null): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(Number(unitAmount) / 100)

/** The endpoint range a band covers, read off the ceilings of it and the one below. */
const bandRange = (tiers: PriceTier[], index: number): string => {
  const from = index === 0 ? 1 : (tiers[index - 1].up_to ?? 0) + 1
  const to = tiers[index].up_to
  return to === null
    ? `${from.toLocaleString()}+`
    : `${from.toLocaleString()} – ${to.toLocaleString()}`
}

const TERM_LABELS: Record<BillingInterval, string> = { month: 'Monthly', year: 'Annual' }

const formatDate = (iso: string | null): string => (iso ? localDate(iso) : '—')

// The free plan's quota resets at 00:00 UTC on the first of the month, a calendar boundary
// shown as that date in every zone; the trial's resets with the trial itself, an instant.
const formatResetDate = (iso: string, plan: string | null | undefined): string =>
  plan === 'trial' ? localDate(iso) : utcCalendarDate(iso)

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400',
  trial: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400',
  expired: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-400',
  suspended: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-400',
  cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
}

/**
 * What a tenant's plan is called on screen.
 *
 * `premium` and `msp_premium` predate the tiered pricing and say nothing a
 * customer can find in the price list, so a subscriber is named by the band
 * their quantity reaches — Starter, Business, Scale, Enterprise — and these
 * labels are only the fallback for a tenant whose band is not known yet.
 */
const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  free: 'Free',
  premium: 'Subscription',
  msp_premium: 'MSP subscription',
}

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return null
  const style = STATUS_STYLES[status] || STATUS_STYLES.cancelled
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

const SubscriptionSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [summary, setSummary] = useState<BillingSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [redirecting, setRedirecting] = useState(false)
  const [usage, setUsage] = useState<EndpointUsage | null>(null)
  const [usageLoading, setUsageLoading] = useState(true)
  const [usageError, setUsageError] = useState<string | null>(null)
  // Exactly what is in the field, as typed — an admin reaching 2,600 passes
  // through 2 and 26, so keystrokes are never corrected. Null means untouched,
  // and the field shows the suggested count until then. Corrected on blur.
  const [quantityText, setQuantityText] = useState<string | null>(null)
  const [term, setTerm] = useState<BillingInterval>('year')
  const [startingTrial, setStartingTrial] = useState(false)
  // Starting the trial is a subscription action: the same role that buys.
  const { canManageSubscription } = useAuthStore()
  const canStartTrial = canManageSubscription()

  const checkoutResult = searchParams.get('checkout')

  const load = useCallback(async () => {
    try {
      const data = await getBillingSummary()
      setSummary(data)
      setError(null)
    } catch (err: any) {
      setError(errorText(err, 'Could not load billing information.'))
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Counting endpoints runs an Intune report export, so it is fetched on its own
   * rather than with the summary — the plan and the pay button must not wait for
   * it. `refresh` forces a recount; the cached figure is up to 30 minutes old.
   */
  const loadUsage = useCallback(async (refresh = false) => {
    setUsageLoading(true)
    setUsageError(null)
    try {
      const counted = await getEndpointUsage(refresh)
      setUsage(counted)
    } catch (err: any) {
      setUsageError(errorText(err, 'Could not count endpoints.'))
    } finally {
      setUsageLoading(false)
    }
  }, [])

  useEffect(() => {
    // Both at once: neither section needs the other to render.
    load()
    loadUsage()
  }, [load, loadUsage])

  const dismissBanner = () => {
    searchParams.delete('checkout')
    setSearchParams(searchParams, { replace: true })
  }

  const goToStripe = async (open: () => Promise<string>) => {
    setRedirecting(true)
    setError(null)
    try {
      window.location.href = await open()
    } catch (err: any) {
      setError(errorText(err, 'Stripe could not be reached. Try again.'))
      setRedirecting(false)
    }
  }

  const startTrialNow = async () => {
    setStartingTrial(true)
    setError(null)
    try {
      setSummary(await startTrial())
      // The packaging notice caches the plan's budget; a trial changes it.
      resetPackagingEntitlementCache()
    } catch (err: any) {
      setError(errorText(err, 'The trial could not be started.'))
    } finally {
      setStartingTrial(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-theme-text-primary">Subscription</h2>
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
          <p className="text-theme-text-secondary">Loading…</p>
        </div>
      </div>
    )
  }

  const licensed = summary?.endpoints_licensed ?? null
  // Null until the count arrives — rendering 0 in the meantime reads as "no
  // endpoints", which is exactly the wrong answer to show an admin.
  const used = usage?.billable ?? null
  const showUsageBar = licensed !== null && licensed > 0 && used !== null
  const overWarnThreshold = showUsageBar && used! / licensed! >= USAGE_WARN_AT
  const usageBarWidth = showUsageBar ? Math.min(100, (used! / licensed!) * 100) : 0

  // One rule for both plans: a free tenant is entitled to the free limit, a
  // subscriber to what they bought, and a trial to no limit at all. Endpoints
  // enrol in the customer's own Intune, so this is always found after the fact.
  const entitled = summary?.endpoints_entitled ?? null
  const overLimit = entitled !== null && used !== null && used > entitled

  // Pricing is graduated, so there is no single per-endpoint rate to multiply —
  // the quantity has to be totalled against the ladder it will be billed on.
  const offeredTerms = summary?.prices ?? []
  const selectedPlan = offeredTerms.find((p) => p.interval === term) ?? null
  const freeLimit = summary?.free_endpoint_limit ?? DEFAULT_FREE_ENDPOINT_LIMIT
  // The smallest license on sale comes from the server. Under free-first
  // onboarding it is the minimum spend (150 endpoints), not one past the free
  // limit; the fallback keeps the legacy rule for an older backend.
  const minPaidEndpoints = summary?.min_paid_endpoints ?? freeLimit + 1
  const freePlan = summary?.free_plan ?? null
  // What the free plan is called on screen, from the terms the server states.
  const freePlanShort = freePlan?.includes_packaging
    ? `every service; up to ${freePlan.packaging_apps_per_month} custom apps and ${freePlan.packaging_jobs_per_month} packaging jobs a month` +
      `${freePlan.includes_managed_tenants ? '' : '; no managed tenants'}; no support`
    : 'every service except packaging'
  const freePlanSentence = freePlan?.includes_packaging
    ? `The free plan includes every service, with packaging limited to ${freePlan.packaging_apps_per_month} custom apps and ` +
      `${freePlan.packaging_jobs_per_month} packaging jobs a month${freePlan.includes_managed_tenants ? '' : ', no managed tenants'}, and no support.`
    : 'The free plan covers every service except packaging, which requires a subscription.'
  // Where the minimum comes from: the free plan's edge, or the minimum spend.
  const minimumReason =
    minPaidEndpoints === freeLimit + 1
      ? `A subscription starts at ${minPaidEndpoints.toLocaleString()} endpoints — the free plan covers the first ${freeLimit.toLocaleString()}.`
      : `A subscription starts at ${minPaidEndpoints.toLocaleString()} endpoints, the smallest license on sale.`
  // What the field offers before the admin types: the count the tenant already
  // runs, so the common case is one click, never below the first billable one.
  const suggestedQuantity = Math.max(minPaidEndpoints, usage?.billable ?? 0)
  const quantityValue = quantityText ?? String(suggestedQuantity)
  const typedQuantity = Number(quantityValue)
  const quantityValid =
    quantityValue.trim() !== '' &&
    Number.isInteger(typedQuantity) &&
    typedQuantity >= minPaidEndpoints &&
    typedQuantity <= MAX_ENDPOINTS
  const buyQuantity = quantityValid ? typedQuantity : suggestedQuantity
  const checkoutTotal =
    selectedPlan && quantityValid ? graduatedTotal(selectedPlan.tiers, buyQuantity) : null
  // The band lines the total is made of, shown so the figure explains itself.
  const checkoutBreakdown =
    selectedPlan && quantityValid ? graduatedBreakdown(selectedPlan.tiers, buyQuantity) : null
  // The ladder charges each band its own rate, so there is no rate to read off
  // the price — what a tenant actually pays per endpoint is the total over the
  // count, and it drops as the count crosses a threshold. Shown to the cent:
  // it is an average, and the exact sum is in the breakdown beside it. Marked
  // approximate whenever the cent hides a fraction, so 501 endpoints reads
  // "≈ $3.00" and never "$2.999".
  const averagePerEndpoint = checkoutTotal !== null ? checkoutTotal / buyQuantity : null
  const averageIsRounded =
    averagePerEndpoint !== null &&
    Math.abs(averagePerEndpoint - Math.round(averagePerEndpoint)) > 1e-9

  // An out-of-range count is never rewritten: the value stays exactly as typed,
  // the field reads invalid with the message below it, and checkout is
  // unavailable until the admin fixes it. Snapping the number under someone's
  // cursor hides the rule instead of stating it.

  // Derived from the two ladders rather than stated, so the badge cannot outlive
  // a price change. Floored: claiming the rounded-up figure overstates it.
  const monthlyPlan = offeredTerms.find((p) => p.interval === 'month') ?? null
  const yearlyPlan = offeredTerms.find((p) => p.interval === 'year') ?? null
  const annualSaving = (() => {
    if (!monthlyPlan || !yearlyPlan) return null
    const perYearOnMonthly = graduatedTotal(monthlyPlan.tiers, buyQuantity)
    const perYearOnAnnual = graduatedTotal(yearlyPlan.tiers, buyQuantity)
    if (!perYearOnMonthly || !perYearOnAnnual) return null
    return Math.floor((1 - perYearOnAnnual / (perYearOnMonthly * 12)) * 100)
  })()

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Subscription</h2>
        <p className="text-theme-text-secondary">
          Your subscription is billed yearly, one licence per managed endpoint.
        </p>
      </div>

      {checkoutResult === 'success' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 flex items-start justify-between">
          <p className="text-sm text-green-800 dark:text-green-300">
            Payment received. Your subscription is active.
          </p>
          <button onClick={dismissBanner} className="text-sm text-green-700 dark:text-green-400 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {checkoutResult === 'cancelled' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 flex items-start justify-between">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Checkout was cancelled. Nothing has been charged.
          </p>
          <button onClick={dismissBanner} className="text-sm text-amber-700 dark:text-amber-400 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {summary && !summary.configured && (
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
          <h3 className="text-lg font-semibold text-theme-text-primary">Subscription is not set up</h3>
          <p className="text-sm text-theme-text-secondary mt-1">
            This deployment has no Stripe credentials configured. Contact your Splashtop
            representative to enable subscription management.
          </p>
        </div>
      )}

      {summary?.configured && (
        <>
          {/* Plan */}
          <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-theme-text-primary">
                    {summary.tier || PLAN_LABELS[summary.plan || ''] || 'No plan'}
                  </h3>
                  <StatusBadge status={summary.status} />
                </div>
                <p className="text-sm text-theme-text-secondary mt-1">
                  {summary.amount_total !== null && summary.interval !== null
                    ? `${formatMoney(summary.amount_total, summary.currency)} per ${summary.interval}, excluding tax`
                    : 'Priced per endpoint'}
                </p>
              </div>
              {overLimit && (
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
                  {used!.toLocaleString()} endpoints are in use, more than the{' '}
                  {entitled!.toLocaleString()} your plan covers.{' '}
                  {summary.has_subscription
                    ? 'Raise the endpoint count from Manage subscription — Stripe prorates the change.'
                    : 'Subscribe to keep packaging, deployment and updates running.'}
                </p>
              )}

              {/*
                Both reasons, one date. A tenant in breach of payment AND of
                quantity must not fix one, see the warning go, and be suspended
                by the other — service stops at whichever grace ends first.
              */}
              {summary.service_stops_at && (
                <div className="mt-3 px-3 py-2 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    Service stops on {formatDate(summary.service_stops_at)}
                  </p>
                  <ul className="mt-1 text-sm text-amber-800 dark:text-amber-300 list-disc list-inside">
                    {summary.payment_deadline && (
                      <li>
                        Payment is overdue — grace ends {formatDate(summary.payment_deadline)}
                      </li>
                    )}
                    {summary.overage_deadline && (
                      <li>
                        More endpoints in use than the plan covers — grace ends{' '}
                        {formatDate(summary.overage_deadline)}
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {!summary.can_package && summary.entitlement_reason && (
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
                  {summary.entitlement_reason}
                </p>
              )}
              {/* The count the compliance sweep kept on the subscription: what the plan
                  is actually judged on, as opposed to the live recount below. */}
              {summary.endpoints_measured !== null && summary.endpoints_measured !== undefined && (
                <p className="text-sm text-theme-text-secondary mt-3">
                  Last recorded count: {summary.endpoints_measured.toLocaleString()} endpoint
                  {summary.endpoints_measured === 1 ? '' : 's'}
                  {entitled !== null ? ` of ${entitled.toLocaleString()} covered` : ''}, taken{' '}
                  {formatDate(summary.endpoints_measured_at ?? null)}.
                </p>
              )}
              {summary.packaging_budget && (
                <p className="text-sm text-theme-text-secondary mt-1">
                  Packaging this {summary.plan === 'trial' ? 'trial' : 'month'}:{' '}
                  {summary.packaging_budget.apps_used} of {summary.packaging_budget.apps_limit} custom apps,{' '}
                  {summary.packaging_budget.jobs_used} of {summary.packaging_budget.jobs_limit} packaging jobs
                  {summary.packaging_budget.resets_at
                    ? ` — resets ${formatResetDate(summary.packaging_budget.resets_at, summary.plan)}`
                    : ''}
                  .
                </p>
              )}
              {summary.has_subscription && (
                <button
                  onClick={() => goToStripe(createBillingPortalSession)}
                  disabled={redirecting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {redirecting ? 'Opening…' : 'Manage subscription'}
                </button>
              )}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-theme-border-primary">
              <div>
                <dt className="text-xs uppercase tracking-wide text-theme-text-secondary">
                  {summary.cancel_at_period_end ? 'Access ends' : 'Renews on'}
                </dt>
                {/* A free row's date is a trial end the admin set, not a renewal. */}
                <dd className="text-sm text-theme-text-primary mt-1">
                  {formatDate(summary.plan === 'free' ? null : summary.expires_at)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-theme-text-secondary">Endpoints licensed</dt>
                <dd className="text-sm text-theme-text-primary mt-1">
                  {licensed !== null ? licensed : 'None purchased'}
                </dd>
              </div>
            </dl>

            {summary.cancel_at_period_end && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-4">
                This subscription is set to cancel at the end of the current period. Reactivate it
                from Manage subscription.
              </p>
            )}

            {summary.error && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-4">{summary.error}</p>
            )}
          </div>

          {/* Endpoint usage */}
          <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-theme-text-primary">Endpoint usage</h3>
                <p className="text-sm text-theme-text-secondary mt-1">
                  {used === null
                    ? 'Counting the endpoints you manage…'
                    : licensed !== null
                      ? `${used} of ${licensed} licensed endpoints in use`
                      : `${used} endpoint${used === 1 ? '' : 's'} in use`}
                </p>
              </div>
              <button
                onClick={() => loadUsage(true)}
                disabled={usageLoading}
                className="text-sm text-blue-500 hover:underline disabled:opacity-50"
              >
                {usageLoading ? 'Counting…' : 'Recount'}
              </button>
            </div>

            {usageLoading && (
              <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-theme-border-primary">
                <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin" />
                <span className="text-sm text-theme-text-secondary">
                  Reading your devices and their install status from Intune. This takes a few
                  seconds.
                </span>
              </div>
            )}

            {usageError && !usageLoading && (
              <div className="mt-4 pt-4 border-t border-theme-border-primary">
                <p className="text-sm text-amber-700 dark:text-amber-400">{usageError}</p>
                <button
                  onClick={() => loadUsage(true)}
                  className="text-sm text-blue-500 hover:underline mt-1"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Where the number came from. Without this, a surprising count
                (especially zero) looks like a bug with no way to check. The
                licensed figure is the managed count; the other three say how far
                our apps have got across it. */}
            {usage && !usageLoading && (
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-theme-border-primary">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-theme-text-secondary">Managed endpoints</dt>
                  <dd className="text-sm text-theme-text-primary mt-1">
                    {usage.managed_endpoints ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-theme-text-secondary">Intune — installed</dt>
                  <dd className="text-sm text-theme-text-primary mt-1">{usage.installed}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-theme-text-secondary">Intune — assigned</dt>
                  <dd className="text-sm text-theme-text-primary mt-1">{usage.targeted}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-theme-text-secondary">Agent devices</dt>
                  <dd className="text-sm text-theme-text-primary mt-1">{usage.agent_devices}</dd>
                </div>
              </dl>
            )}

            {usage && !usageLoading && usage.intune_available && usage.inventory_error && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
                Your Intune device list could not be read, so this counts only the endpoints our
                apps have reached. Grant <code>DeviceManagementManagedDevices.Read.All</code> to
                the app registration under{' '}
                <Link to="/settings/integrations/intune" className="underline">Intune integration</Link>{' '}
                to count every managed endpoint.
              </p>
            )}

            {usage && !usageLoading && !usage.intune_available && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
                Intune is not connected, so only agent-registered endpoints are counted.{' '}
                <Link to="/settings/integrations/intune" className="underline">Connect Intune</Link> to
                count the endpoints you deploy to.
              </p>
            )}

            {usage && !usageLoading && usage.intune_available && usage.apps_checked === 0 && (
              <p className="text-sm text-theme-text-secondary mt-3">
                No apps have been published to Intune yet, so nothing has reached these endpoints.
              </p>
            )}

            {usage && !usageLoading && usage.apps_failed > 0 && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
                {usage.apps_failed} app{usage.apps_failed === 1 ? '' : 's'} could not be read from
                Intune, so the installed and assigned figures may be low.
              </p>
            )}

            {showUsageBar && (
              <div className="mt-4">
                <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${overWarnThreshold ? 'bg-amber-500' : 'bg-blue-600'}`}
                    style={{ width: `${usageBarWidth}%` }}
                  />
                </div>
                {overWarnThreshold && (
                  <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
                    You are close to your licensed endpoint count. Add licences from Manage
                    subscription — Stripe prorates the change.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* The one free trial, offered from the free plan under free-first onboarding */}
          {summary.trial_available && (
            <div
              data-testid="trial-offer"
              className="bg-theme-secondary border border-blue-400/60 rounded-lg p-6"
            >
              {/* The term is this tenant's: an end the admin set on the row, else the default. */}
              <h3 className="text-lg font-semibold text-theme-text-primary">
                Try the full product free for {summary.trial_days ?? DEFAULT_TRIAL_DAYS}{' '}
                {(summary.trial_days ?? DEFAULT_TRIAL_DAYS) === 1 ? 'day' : 'days'}
              </h3>
              <p className="text-sm text-theme-text-secondary mt-1">
                No endpoint limit, up to 10 custom apps and 20 packaging jobs, managed tenants
                included. When the trial ends you return to the free plan unless you subscribe.
                One trial per tenant.
              </p>
              {canStartTrial ? (
                <button
                  onClick={startTrialNow}
                  disabled={startingTrial}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {startingTrial ? 'Starting…' : 'Start free trial'}
                </button>
              ) : (
                <p className="text-sm text-theme-text-secondary mt-3">
                  The tenant owner starts the trial.
                </p>
              )}
            </div>
          )}

          {/* First purchase */}
          {!summary.has_subscription && (
            <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
              <h3 className="text-lg font-semibold text-theme-text-primary">Start a subscription</h3>
              <p className="text-sm text-theme-text-secondary mt-1">
                {minPaidEndpoints === freeLimit + 1
                  ? `Your first ${freeLimit.toLocaleString()} endpoints are covered by the free plan, so a subscription starts at ${minPaidEndpoints.toLocaleString()}. Above that, every endpoint is billed.`
                  : `The free plan covers up to ${freeLimit.toLocaleString()} endpoints. A subscription starts at ${minPaidEndpoints.toLocaleString()} endpoints, the smallest license on sale, and bills every endpoint from the first.`}{' '}
                You can change the count later at any time.
              </p>
              <p className="text-sm text-theme-text-secondary mt-2">{freePlanSentence}</p>

              {offeredTerms.length > 1 && (
                <div
                  role="radiogroup"
                  aria-label="Billing term"
                  className="inline-flex mt-4 p-1 bg-theme-primary border border-theme-border-primary rounded-lg"
                >
                  {(['month', 'year'] as BillingInterval[])
                    .filter((t) => offeredTerms.some((p) => p.interval === t))
                    .map((t) => (
                      <button
                        key={t}
                        role="radio"
                        aria-checked={term === t}
                        onClick={() => setTerm(t)}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium ${
                          term === t
                            ? 'bg-theme-secondary text-theme-text-primary shadow-sm'
                            : 'text-theme-text-secondary'
                        }`}
                      >
                        {TERM_LABELS[t]}
                        {t === 'year' && annualSaving !== null && annualSaving > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs">
                            −{annualSaving}%
                          </span>
                        )}
                      </button>
                    ))}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-4 mt-4">
                <div>
                  <label
                    htmlFor="endpoint-quantity"
                    className="block text-xs uppercase tracking-wide text-theme-text-secondary mb-1"
                  >
                    Endpoints
                  </label>
                  <input
                    id="endpoint-quantity"
                    type="number"
                    min={minPaidEndpoints}
                    max={MAX_ENDPOINTS}
                    aria-invalid={!quantityValid}
                    aria-describedby={quantityValid ? undefined : 'endpoint-quantity-hint'}
                    value={quantityValue}
                    onChange={(e) => setQuantityText(e.target.value)}
                    className={`w-32 px-3 py-2 bg-theme-primary border rounded-lg text-theme-text-primary ${
                      quantityValid
                        ? 'border-theme-border-primary'
                        : 'border-red-500 focus:outline-red-500'
                    }`}
                  />
                </div>

                {checkoutTotal !== null && (
                  <div className="pb-2">
                    <p className="text-xs uppercase tracking-wide text-theme-text-secondary">
                      Total per {term}, excluding tax
                    </p>
                    <p className="text-sm text-theme-text-primary mt-1">
                      {formatMoney(checkoutTotal, selectedPlan?.currency ?? summary.currency)}
                    </p>
                    <p className="text-xs text-theme-text-secondary mt-1">
                      Tax is calculated at checkout from your billing address.
                    </p>
                    {checkoutBreakdown !== null && checkoutBreakdown.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {checkoutBreakdown.map((line) => (
                          <p key={line.name} className="text-xs text-theme-text-secondary">
                            {`${line.name}: ${line.units.toLocaleString()} × ${formatRate(
                              line.unit_amount_decimal,
                              selectedPlan?.currency ?? summary.currency,
                            )} = ${formatRate(
                              line.subtotal,
                              selectedPlan?.currency ?? summary.currency,
                            )}`}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {averagePerEndpoint !== null && (
                  <div className="pb-2">
                    <p className="text-xs uppercase tracking-wide text-theme-text-secondary">
                      Average per endpoint / {term}
                    </p>
                    <p className="text-sm text-theme-text-primary mt-1">
                      {`${averageIsRounded ? '≈ ' : ''}${formatMoney(
                        averagePerEndpoint,
                        selectedPlan?.currency ?? summary.currency,
                      )}`}
                    </p>
                  </div>
                )}

                <button
                  onClick={() => goToStripe(() => createBillingCheckoutSession(buyQuantity, term))}
                  disabled={redirecting || !quantityValid}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {redirecting ? 'Opening…' : 'Continue to payment'}
                </button>
              </div>

              {!quantityValid && (
                <p
                  id="endpoint-quantity-hint"
                  className="text-sm text-amber-700 dark:text-amber-400 mt-2"
                >
                  {typedQuantity > MAX_ENDPOINTS
                    ? `More than ${MAX_ENDPOINTS.toLocaleString()} endpoints has to be arranged with your Splashtop representative.`
                    : minimumReason}
                </p>
              )}

              {selectedPlan && selectedPlan.tiers.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-theme-text-primary">Pricing</h4>

                  {/*
                    The free plan sits above the ladder rather than inside it. It is a plan, not a
                    band: a subscription bills every endpoint from the first, so a "1 – 150 free"
                    row among the paid rows would misstate what the invoice comes to.
                  */}
                  <div className="mt-3 flex items-center justify-between gap-4 px-3 py-2 border border-theme-border-primary rounded-lg">
                    <div>
                      <p className="text-sm text-theme-text-primary">
                        1 – {freeLimit.toLocaleString()} endpoints
                      </p>
                      <p className="text-xs text-theme-text-secondary mt-0.5">
                        Free plan — {freePlanShort}, no subscription needed
                      </p>
                    </div>
                    <p className="text-sm font-medium text-theme-text-primary">Free</p>
                  </div>

                  <p className="text-xs text-theme-text-secondary mt-4">
                    Above {freeLimit.toLocaleString()} endpoints a subscription bills every endpoint,
                    including the first {freeLimit.toLocaleString()}. Each band's rate applies to the
                    endpoints within it, so a larger subscription pays the lower rates on the
                    endpoints above each threshold.
                    {minPaidEndpoints !== freeLimit + 1 &&
                      ` A subscription starts at ${minPaidEndpoints.toLocaleString()} endpoints. A fair-use policy applies to custom app packaging on paid plans.`}
                  </p>
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full text-sm border border-theme-border-primary rounded-lg">
                      <thead>
                        <tr className="text-left text-theme-text-secondary">
                          <th className="px-3 py-2 font-medium">Tier</th>
                          <th className="px-3 py-2 font-medium">Endpoints</th>
                          <th className="px-3 py-2 font-medium">
                            Per endpoint / {term}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPlan.tiers.map((tier, index) => (
                          <tr
                            key={tier.up_to ?? 'top'}
                            className="border-t border-theme-border-primary text-theme-text-primary"
                          >
                            <td className="px-3 py-2">{tier.name}</td>
                            <td className="px-3 py-2">{bandRange(selectedPlan.tiers, index)}</td>
                            <td className="px-3 py-2">
                              {formatRate(tier.unit_amount_decimal, selectedPlan.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="text-xs text-theme-text-secondary mt-4">
                Payment is handled by Stripe. Card details never reach this platform.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SubscriptionSettings
